<template>
  <v-content class="tools page bundle-errors">
    <v-container>
      <v-toolbar flat class="transparent">
        <v-breadcrumbs
          class="pl-0"
          :items="[
            { exact: true, text: 'Admin', to: '/dashboard' },
            { exact: true, text: 'Verification', to: '/tools/verification' },
          ]"
        >
          <v-icon slot="divider">mdi-chevron-right</v-icon>
        </v-breadcrumbs>

        <v-spacer />

        <v-btn flat icon @click="refresh">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-toolbar>

      <can I="verify" a="ProfileQualification">
        <v-card dark toolbar>
          <v-toolbar flat class="transparent">
            <div>
              <div class="headline">Unverified Profile Qualifications</div>
              <div class="subheading grey--text">Qualifications that are not yet verified</div>
            </div>

            <v-spacer />
            <v-btn
              small
              flat
              fab
              @click="showFilters = !showFilters"
              :color="filtersApplied ? 'primary' : ''"
            >
              <v-icon>mdi-filter-variant</v-icon>
            </v-btn>
          </v-toolbar>
          <v-divider />

          <!-- Filters -->
          <transition name="slide-y-transition">
            <v-card-text v-show="showFilters">
              <InstitutionSelect
                :value="filters.institution ? { id: filters.institution } : null"
                @input="setInstitution"
              />
              <OpportunitySelect
                :value="filters.opportunity"
                @input="setOpportunity"
              />
              <OpportunityPhaseSelect
                v-if="filters.opportunity"
                :opportunity-id="filters.opportunity"
                :value="filters.phase ? { id: filters.phase } : null"
                @input="setPhase"
              />
            </v-card-text>
          </transition>

          <v-data-table
            :headers="headers"
            :loading="loading"
            :items="profileQualifications"
            hide-actions
            disable-initial-sort
          >
            <template slot="items" slot-scope="{ item }">
              <tr>
                <td>{{ item.id }}</td>
                <td>
                  <router-link :to="`/profiles/${item.profile.id}`">
                    {{ item.profile.fullName }}
                  </router-link>
                </td>
                <td>
                  <span v-if="item.institution && item.institution.id">
                    <v-tooltip right v-if="item.institution && item.institution.acronym">
                      <router-link slot="activator" :to="`/institutions/${item.institution.id}`">
                        {{ item.institution.acronym }}
                      </router-link>
                      <span>{{ item.institutionName }}</span>
                    </v-tooltip>
                    <router-link v-else :to="`/institutions/${item.institution.id}`">
                      {{ item.institutionName }}
                    </router-link>
                  </span>
                  <span v-else>{{ item.institutionName }}</span>
                </td>
                <td>
                  <router-link :to="`/qualifications/${item.qualification.id}`">
                    {{ item.qualificationTypeName }}
                    <span v-if="item.specialization">({{ item.specialization }})</span>
                  </router-link>
                </td>
                <td>{{ parseInt(item.gradeNormalized) }} %</td>
                <td>{{ new Date(item.updatedAt) | date('d/M/yyyy HH:mm') }}</td>
                <td>
                  <Flag
                    v-for="(f, i) in item.flags"
                    :key="`qualification-flag-${i}`"
                    :value="f"
                    icon
                  />
                </td>
                <td>
                  <v-tooltip left color="grey darken-3">
                    <v-btn
                      small
                      flat
                      fab
                      slot="activator"
                      color="success"
                      @click="viewing = item.id"
                    >
                      <v-icon>mdi-eye</v-icon>
                    </v-btn>
                    <span>Review profile qualification</span>
                  </v-tooltip>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </can>
      <!-- Profile Qualification Dialog -->
      <v-dialog :value="viewing" @update:returnValue="viewing = null" width="70vw" lazy>
        <v-card dark :key="viewing" v-if="profileQualification">
          <v-card-title>
            <div>
              <div class="title">
                {{ profileQualification.profile.fullName }}
              </div>
              <div class="subheading grey--text">
                {{ profileQualification.qualificationTypeName }}
                <span v-if="profileQualification.specialization">
                  ({{ profileQualification.specialization }})
                </span>
                from {{ profileQualification.institutionName }}
              </div>
            </div>
            <v-spacer />

            <Flag
              v-for="(f, i) in profileQualification.flags"
              :key="`qualification-flag-${i}`"
              :value="f"
              icon
            />
            <can I="create" a="Flag">
              <CreateFlag
                :target="{ ...profileQualification, type: 'ProfileQualification' }"
                :profile="profileQualification.profile"
                :filter="['grade_value', 'qualification_dates', 'transcript', 'qualification']"
                @done="refresh() && skip()"
              >
                <v-btn icon flat color="error" slot="activator">
                  <v-icon>mdi-flag</v-icon>
                </v-btn>
              </CreateFlag>
            </can>
          </v-card-title>
          <v-divider></v-divider>
          <!-- Attributes table -->
          <table class="v-table theme--dark">
            <tbody>
              <tr>
                <th class="text-xs-right">Identity Number</th>
                <td>
                  {{ profileQualification.profile.identification }}
                </td>
              </tr>
              <tr>
                <th class="text-xs-right">Started</th>
                <td v-if="profileQualification.startsAt">
                  {{ new Date(profileQualification.startsAt) | date('d MMMM yyyy') }}
                  ({{ new Date(profileQualification.startsAt) | inWords({ addSuffix: true }) }})
                </td>
                <td v-else><em>Not set</em></td>
              </tr>
              <tr>
                <th class="text-xs-right">Completed</th>
                <td v-if="profileQualification.endsAt">
                  {{ new Date(profileQualification.endsAt) | date('d MMMM yyyy') }}
                  ({{ new Date(profileQualification.endsAt) | inWords({ addSuffix: true }) }})
                </td>
                <td v-else><em>Not set</em></td>
              </tr>
              <tr>
                <th class="text-xs-right">Accredited</th>
                <td v-if="profileQualification.accreditedAt">
                  {{ new Date(profileQualification.accreditedAt) | date('d MMMM yyyy') }}
                  ({{ new Date(profileQualification.accreditedAt) | inWords({ addSuffix: true }) }})
                </td>
                <td v-else><em>Not set</em></td>
              </tr>

              <tr>
                <th class="text-xs-right">Grade</th>
                <td v-if="profileQualification.gradeValue">
                  {{ profileQualification.gradeValue }} ({{ profileQualification.gradeScale }})
                </td>
                <td v-else><em>Not set</em></td>
              </tr>

              <tr>
                <th class="text-xs-right">Proof of Registration</th>
                <td v-if="profileQualification.proofOfRegistration">
                  <a :href="profileQualification.proofOfRegistration.url" target="_blank">
                    View proof of registration
                  </a>
                </td>
                <td v-else><em>Not provided</em></td>
              </tr>

              <tr>
                <th class="text-xs-right">Transcript</th>
                <td v-if="profileQualification.transcript">
                  <a :href="profileQualification.transcript.url" target="_blank">
                    View transcript
                  </a>
                </td>
                <td v-else><em>Not provided</em></td>
              </tr>
            </tbody>
          </table>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer />
            <v-btn @click="skip()" color="warning">Skip</v-btn>
            <v-btn @click="verify(profileQualification)" color="success">
              <v-icon left>mdi-check</v-icon> Verify
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-content>
</template>

<script>
// Components
import CreateFlag from '@/components/flags/CreateFlag'
import Flag from '@/components/flags/Flag'
import InstitutionSelect from '@/components/selects/InstitutionSelect'
import OpportunitySelect from '@/components/selects/OpportunitySelect'
import OpportunityPhaseSelect from '@/components/selects/OpportunityPhaseSelect'

// Helpers
import _ from 'lodash'

// Mixins
import { managedQueryParameters } from '@/mixins/managedQueryParameters.js'

// Vue Component
export default {
  components: { CreateFlag, Flag, InstitutionSelect, OpportunitySelect, OpportunityPhaseSelect },
  mixins: [managedQueryParameters],
  created() {
    this.refresh()
  },
  data() {
    return {
      profileQualifications: [],
      loading: true,
      viewing: null,
      showFilters:
        _.get(this.$route, 'query.opportunity') ||
        _.get(this.$route, 'query.institution') ||
        _.get(this.$route, 'query.phase'),
      filters: {
        institution: this.$route.query.institution ? parseInt(this.$route.query.institution) : null,
        opportunity: this.$route.query.opportunity ? parseInt(this.$route.query.opportunity) : null,
        phase: this.$route.query.phase ? parseInt(this.$route.query.phase) : null,
      },
      // Table Configuration
      headers: [
        { text: 'ID', sortable: false },
        { text: 'Profile', sortable: false },
        { text: 'Institution', sortable: false },
        { text: 'Qualification', sortable: false },
        { text: 'Grade', sortable: false },
        { text: 'Last updated', sortable: false },
        { text: 'Flags', sortable: false },
        { text: '', sortable: false },
      ],
    }
  },
  computed: {
    profileQualification() {
      if (!this.viewing) return null
      return this.profileQualifications.find(
        profileQualification => profileQualification.id == this.viewing
      )
    },
    filtersApplied() {
      return this.filters.opportunity || this.filters.institution || this.filters.phase
    },
  },
  watch: {
    filters: {
      handler: function() {
        this.updateQueryParameters(this.filters)
        this.refresh()
      },
      deep: true,
    },
  },
  methods: {
    refresh() {
      this.loading = true
      this.$http
        .get(`/admin/tools/unverified_profile_qualifications${window.location.search}`)
        .then(({ data }) => {
          this.profileQualifications = data
          this.loading = false
        })
    },
    setInstitution(institution) {
      if (institution) {
        this.filters.institution = institution.id
      } else {
        this.filters.institution = null
      }
    },
    setOpportunity(opportunity) {
      if (opportunity) {
        this.filters.opportunity = opportunity
      } else {
        this.filters.opportunity = null
      }
      // Reset phase when opportunity changes
      this.filters.phase = null
    },
    setPhase(phaseId) {
      if (phaseId) {
        this.filters.phase = phaseId
      } else {
        this.filters.phase = null
      }
    },
    skip() {
      const index = this.profileQualifications.indexOf(this.profileQualification)
      this.viewing = null
      // If there is another one in the list make it the viewed one
      if (this.profileQualifications[index + 1])
        this.viewing = this.profileQualifications[index + 1].id
    },
    verify(profileQualification) {
      const index = this.profileQualifications.indexOf(profileQualification)
      this.$store.dispatch('admin/profileQualifications/verify', profileQualification).then(() => {
        // Remove the profile qualification as it is now verified
        this.profileQualifications.splice(index, 1)
        // If there is another one in the list make it the viewed one
        if (this.profileQualifications.length > index) {
          this.viewing = this.profileQualifications[index].id
        } else {
          this.viewing = null
        }
      })
    },
  },
}
</script>
